import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { intlGet } from 'utils/intlGet';
import { TagSelectProps } from './models';
import styles from './styles.module.scss';
import { byPrefix } from './utils';

const Tag: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <span className={styles.TagSelect__tag}>
    <span className={styles.TagSelect__tag__text} title={label}>{label}</span>
    <button
      type='button'
      className={styles.TagSelect__tag__remove}
      aria-label={`Remove ${label}`}
      onClick={onRemove}
    >
      ×
    </button>
  </span>
);

const TagSelect: React.FC<TagSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search values...',
  noMatchesText = 'No match found.',
  singleSelect = false,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [internal, setInternal] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showError, setShowError] = useState(false);

  const selected = value ?? internal;
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    return options.filter(option => byPrefix(option, query));
  }, [options, query]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (!rootRef.current) {
        return;
      }
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);

    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const setSelected = useCallback((next: string[]): void => {
    onChange?.(next);

    if (!value) {
      setInternal(next);
    }
  }, [onChange, value]);

  const toggle = useCallback((val: string): void => {
    setQuery('');

    if (singleSelect) {
      setSelected([val]);

      return;
    }

    setSelected(
      selectedSet.has(val)
        ? selected.filter(x => x !== val)
        : [...selected, val],
    );
  }, [selected, selectedSet, singleSelect, setSelected, setQuery]);

  const remove = useCallback((val: string): void => {
    if (!selectedSet.has(val)) {
      return;
    }
    setSelected(selected.filter(item => item !== val));
  }, [selectedSet, setSelected, selected]);

  const clearAll = useCallback((): void => {
    setSelected([]);
  }, [setSelected]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && query === '' && selected.length) {
      e.preventDefault();
      remove(selected[selected.length - 1]);
    }
  }, [query, remove, selected]);

  const handleClick = useCallback((): void => {
    setOpen(true);
    inputRef.current?.focus();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
    setOpen(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>): void => {
    const root = e.currentTarget;
    const next = e.relatedTarget as Node | null;

    if ((!next || !root.contains(next)) && !selected.length) {
      setShowError(true);
    }
  }, [selected.length]);

  const renderTags = (): React.ReactElement[] => {
    return selected.map((val) => {
      const label = options.find(option => option === val) ?? val;

      return <Tag key={val} label={label} onRemove={(): void => remove(val)} />;
    });
  };

  const inputType = singleSelect ? 'radio' : 'checkbox';
  const hasError = showError && !selected.length && !open;

  return (
    <div
      ref={rootRef}
      className={styles.TagSelect}
      onBlur={handleBlur}>
      <div
        onClick={handleClick}
        className={classNames(
          styles.TagSelect__field,
          {
            [styles.TagSelect__field__error]: hasError,
          },
        )}
      >
        <Icon type={IconType.ZOOM_OUT} size='sm' />
        {renderTags()}
        <input
          ref={inputRef}
          className={styles.TagSelect__input}
          placeholder={selected.length ? '' : placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
        />
      </div>

      {hasError && (
        <div className={styles.TagSelect__error}>
          <span>{intlGet('TagSelect', 'Error')}</span>
        </div>
      )}

      {open && (
        <div className={styles.TagSelect__dropdown}>
          <div className={styles.TagSelect__list}>
            {!singleSelect && <button
              onClick={clearAll}
              className={styles.TagSelect__list__clearAll}
            >
              <Icon className={styles.TagSelect__list__clearAll__icon}
                type={IconType.CLOSE}
                size='sm-md'
                hexColor={'#0072DC'} />
              <span className={styles.TagSelect__list__clearAll__text}>Clear All</span>
            </button>}
            <ul className={styles.TagSelect__list__body}>
              {filtered.length === 0 ? (
                <li className={styles.TagSelect__list__noMatches}>{noMatchesText}</li>
              ) : (
                filtered.map((option) => {
                  const checked = selectedSet.has(option);

                  return (
                    <li key={option}>
                      <button
                        className={styles.TagSelect__list__option}
                        onClick={(): void => toggle(option)}
                      >
                        <input
                          type={inputType}
                          readOnly={true}
                          checked={checked}
                          className={styles.TagSelect__list__option__checkbox}
                        />
                        <span
                          className={styles.TagSelect__list__option__label}
                          title={option}>{option}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSelect;
